import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react'

const WishlistContext = createContext(null)
const STORAGE_KEY = 'slimfast_wishlist'

function readWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function wishlistReducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const product = action.payload
      if (state.some((item) => item.id === product.id)) return state
      return [
        ...state,
        {
          id: product.id,
          name: product.name,
          slug: product.slug || '',
          sku: product.sku || '',
          price: Number(product.price) || 0,
          compareAtPrice: product.compareAtPrice ?? null,
          image: product.image || '',
          stock: product.stock,
          onSale: product.onSale,
        },
      ]
    }
    case 'REMOVE':
      return state.filter((item) => item.id !== action.id)
    case 'TOGGLE': {
      const product = action.payload
      if (state.some((item) => item.id === product.id)) {
        return state.filter((item) => item.id !== product.id)
      }
      return wishlistReducer(state, { type: 'ADD', payload: product })
    }
    case 'CLEAR':
      return []
    default:
      return state
  }
}

export function WishlistProvider({ children }) {
  const [items, dispatch] = useReducer(wishlistReducer, [], readWishlist)

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  }, [items])

  const value = useMemo(
    () => ({
      items,
      itemCount: items.length,
      isInWishlist: (id) => items.some((item) => item.id === id),
      addToWishlist: (product) => dispatch({ type: 'ADD', payload: product }),
      removeFromWishlist: (id) => dispatch({ type: 'REMOVE', id }),
      toggleWishlist: (product) => dispatch({ type: 'TOGGLE', payload: product }),
      clearWishlist: () => dispatch({ type: 'CLEAR' }),
    }),
    [items],
  )

  return (
    <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>
  )
}

export function useWishlist() {
  const ctx = useContext(WishlistContext)
  if (!ctx) {
    throw new Error('useWishlist must be used within WishlistProvider')
  }
  return ctx
}
